import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Box, Chip, Typography } from '@mui/material';

import { queries } from 'RestAPIs/rapini';
import { ControlledAutoComplete } from 'components/shared/ControlledFields';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import ControlledFavoriteAutocomplete from 'components/shared/ControlledFields/ControlledFavoriteAutocomplete';

import type { Control } from 'react-hook-form';
import type { WorkPlaceResponse } from 'generated/index';
import type { OrganizationalUnit } from 'components/types';
import type { FieldKey } from '../input-field-grants';
import type { Event } from 'components/roster-portal/activity-planning/types';

type Props = {
  enable?: boolean;
  control: Control<any>;
  event?: Event;
  organizationalUnit?: OrganizationalUnit;
  organizationalUnits?: OrganizationalUnit[];
  hasFieldGrant: (key: FieldKey) => boolean;
  handleWorkPlaceSelect: (
    project: WorkPlaceResponse | undefined | null
  ) => void;
};

const WhereSection = ({
  enable,
  control,
  event,
  organizationalUnit,
  organizationalUnits,
  hasFieldGrant,
  handleWorkPlaceSelect
}: Props) => {
  const { t } = useTranslation();

  if (!enable) return null;

  return (
    <MultiStepForm.Section
      title="Waar"
      preview={organizationalUnit?.name}
      isEditable={hasFieldGrant('WHERE_IS_EDITABLE')}
      validateFields={[
        'organizationalUnit',
        ...(hasFieldGrant('WORKPLACE_ENTITY_ID') ? ['workPlaceEntityId'] : [])
      ]}
    >
      <OrganizationalUnitSelect
        control={control}
        organizationalUnits={organizationalUnits}
      />
      {hasFieldGrant('WORKPLACE_SELECT') && (
        <WorkplaceSelect
          control={control}
          organizationalUnitId={organizationalUnit?.id}
          handleSelect={handleWorkPlaceSelect}
        />
      )}
      {hasFieldGrant('APPOINTMENT_GROUPING_PREVIEW') &&
        event?.appointmentGrouping && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('activityPlanning.route')}
            </Typography>
            <Chip size="small" label={event.appointmentGrouping.description} />
          </Box>
        )}
    </MultiStepForm.Section>
  );
};

const OrganizationalUnitSelect = ({
  control,
  organizationalUnits
}: {
  control: Control<any>;
  organizationalUnits: any;
}) => {
  const { t } = useTranslation();

  return (
    <ControlledAutoComplete
      name="organizationalUnit"
      control={control}
      label={t('planUnits')}
      variant="outlined"
      options={organizationalUnits ?? []}
      disableSorting
      fullWidth
      idProperty={'id'}
    />
  );
};

const WorkplaceSelect = ({
  control,
  organizationalUnitId,
  handleSelect
}: {
  control: Control<any>;
  organizationalUnitId: any;
  handleSelect?: (project: WorkPlaceResponse | undefined) => void;
}) => {
  const { t } = useTranslation();
  const { data: workPlaces } = queries.useGetWorkPlaces(
    dayjs().format('YYYY-MM-DD'),
    undefined,
    organizationalUnitId
  );

  return (
    <ControlledFavoriteAutocomplete
      type={'WORKPLACE'}
      name="workPlace"
      control={control}
      label={t('workPlace')}
      variant="outlined"
      options={workPlaces ?? []}
      disableSorting
      disableFavoriteFilter
      fullWidth
      onChange={handleSelect}
      idProperty={'entityId'}
    />
  );
};

export default WhereSection;
